/**
 * TaxonomySchemaBuilder component - Main schema editor
 * Allows creating and editing taxonomy schemas with fields, options, and hierarchies
 */

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Loader2, Plus, Save } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';
import type { TaxonomyField, TaxonomySchema } from '@/types/taxonomy';

import { SortableTaxonomyField } from './SortableTaxonomyField';

// ============================================================================
// TYPES
// ============================================================================

export interface TaxonomySchemaBuilderProps {
  schema: TaxonomySchema | null;
  onSave: (fields: Omit<TaxonomyField, 'createdAt'>[]) => Promise<void>;
  isLoading?: boolean;
  isSaving?: boolean;
}

interface FieldErrors {
  [fieldId: string]: {
    name?: string;
    options?: string;
    parentFieldId?: string;
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function generateFieldId(): string {
  return `fld_${Math.random().toString(36).substring(2, 12)}`;
}

function generateOptionId(): string {
  return `opt_${Math.random().toString(36).substring(2, 12)}`;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function TaxonomySchemaBuilder({
  schema,
  onSave,
  isLoading = false,
  isSaving = false,
}: TaxonomySchemaBuilderProps) {
  const [fields, setFields] = useState<TaxonomyField[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});

  // Initialize fields from schema
  useEffect(() => {
    if (schema?.fields) {
      setFields(schema.fields);
      setHasChanges(false);
    } else {
      setFields([]);
    }
  }, [schema]);

  // ============================================================================
  // VALIDATION
  // ============================================================================

  const validateSchema = useCallback((): boolean => {
    const newErrors: FieldErrors = {};
    let isValid = true;

    // Check for unique field names
    const fieldNames = new Set<string>();

    fields.forEach((field) => {
      const fieldErrors: FieldErrors[string] = {};

      // Validate field name
      if (!field.name.trim()) {
        fieldErrors.name = 'Field name is required';
        isValid = false;
      } else if (fieldNames.has(field.name.toLowerCase())) {
        fieldErrors.name = 'Field name must be unique';
        isValid = false;
      } else {
        fieldNames.add(field.name.toLowerCase());
      }

      // Validate options
      if (field.options.length === 0) {
        fieldErrors.options = 'At least one option is required';
        isValid = false;
      } else {
        // Check for empty option labels
        const hasEmptyLabel = field.options.some((opt) => !opt.label.trim());
        if (hasEmptyLabel) {
          fieldErrors.options = 'All options must have labels';
          isValid = false;
        }

        // Check for unique option labels
        const optionLabels = field.options.map((opt) => opt.label.toLowerCase());
        if (new Set(optionLabels).size !== optionLabels.length) {
          fieldErrors.options = 'Option labels must be unique within a field';
          isValid = false;
        }
      }

      // Validate parent field reference
      if (field.parentFieldId) {
        const parentExists = fields.some((f) => f.fieldId === field.parentFieldId);
        if (!parentExists) {
          fieldErrors.parentFieldId = 'Parent field no longer exists';
          isValid = false;
        }
      }

      if (Object.keys(fieldErrors).length > 0) {
        newErrors[field.fieldId] = fieldErrors;
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [fields]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleAddField = () => {
    const newField: TaxonomyField = {
      fieldId: generateFieldId(),
      name: '',
      options: [
        { optionId: generateOptionId(), label: '' },
      ],
      position: fields.length,
      createdAt: new Date().toISOString(),
    };
    setFields([...fields, newField]);
    setHasChanges(true);
  };

  const handleUpdateField = (index: number, updatedField: TaxonomyField) => {
    const newFields = [...fields];
    newFields[index] = updatedField;
    setFields(newFields);
    setHasChanges(true);

    // Clear errors for this field
    if (errors[updatedField.fieldId]) {
      const newErrors = { ...errors };
      delete newErrors[updatedField.fieldId];
      setErrors(newErrors);
    }
  };

  const handleRemoveField = (index: number) => {
    const fieldToRemove = fields[index];
    if (!fieldToRemove) return;

    const newFields = fields.filter((_, i) => i !== index);
    const removedFieldId = fieldToRemove.fieldId;

    // Update positions and clear parent references to removed field
    const updatedFields = newFields.map((field, i) => ({
      ...field,
      position: i,
      parentFieldId:
        field.parentFieldId === removedFieldId
          ? undefined
          : field.parentFieldId,
      parentOptionConditions:
        field.parentFieldId === removedFieldId
          ? undefined
          : field.parentOptionConditions,
    }));

    setFields(updatedFields);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!validateSchema()) {
      return;
    }

    // Strip createdAt for API call
    const fieldsToSave = fields.map(({ createdAt: _createdAt, ...rest }) => rest);
    await onSave(fieldsToSave as Omit<TaxonomyField, 'createdAt'>[]);
    setHasChanges(false);
  };

  // ============================================================================
  // DRAG AND DROP
  // ============================================================================

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex((f) => f.fieldId === active.id);
      const newIndex = fields.findIndex((f) => f.fieldId === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedFields = arrayMove(fields, oldIndex, newIndex).map(
          (field, index) => ({
            ...field,
            position: index,
          })
        );
        setFields(reorderedFields);
        setHasChanges(true);
      }
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-brand animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-primary mb-1">
            Taxonomy Schema
          </h3>
          <p className="text-xs text-secondary">
            Define classification fields for automatic creative tagging.
            The AI will extract values for each field when analyzing videos.
          </p>
        </div>
        <Button
          onClick={handleAddField}
          variant="secondary"
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          disabled={isSaving}
        >
          Add Field
        </Button>
      </div>

      {/* Schema Info */}
      {schema && (
        <div className="text-xs text-muted">
          Version {schema.version} | Last updated:{' '}
          {new Date(schema.updatedAt).toLocaleDateString()}
        </div>
      )}

      {/* Fields */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={fields.map((f) => f.fieldId)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-4">
            {fields.map((field, index) => (
              <SortableTaxonomyField
                key={field.fieldId}
                field={field}
                onChange={(updated) => handleUpdateField(index, updated)}
                onRemove={() => handleRemoveField(index)}
                allFields={fields}
                isLoading={isSaving}
                errors={errors[field.fieldId]}
              />
            ))}

            {fields.length === 0 && (
              <div className="text-center py-12 border-2 border-dashed border-border-subtle rounded-lg">
                <p className="text-sm text-muted mb-4">
                  No taxonomy fields defined yet.
                </p>
                <Button
                  onClick={handleAddField}
                  variant="secondary"
                  leftIcon={<Plus className="h-4 w-4" />}
                  disabled={isSaving}
                >
                  Add Your First Field
                </Button>
              </div>
            )}
          </div>
        </SortableContext>
      </DndContext>

      {/* Save Button */}
      {fields.length > 0 && (
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border-subtle">
          <Button
            onClick={handleSave}
            leftIcon={<Save className="h-4 w-4" />}
            loading={isSaving}
            disabled={!hasChanges || isSaving}
          >
            Save Schema
          </Button>
        </div>
      )}
    </div>
  );
}
